import { PostOfficeAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { type Context } from '@atlassian/post-office-context';
import { RemoteComponent } from '@atlassian/remote-module-loader/remote-component';
import { type FunctionComponent, type PropsWithChildren, type ReactElement, useMemo } from 'react';

import { ANALYTICS_CHANNEL, ANALYTICS_SOURCE } from './constants';
import { requestInitialData } from './initialDataClient';
import { type PluginEnv, type PluginVariant } from './plugin';
import PluginRegistry from './pluginRegistry';
import { useStableObject } from './useStableObject';

const PLACEMENT_PATH = `/api/v1/placements/`;

interface BaseProps {
	placementId: string;
	placementProps?: Record<string, unknown>;
	ErrorFallback?: ReactElement | null;
	Fallback?: ReactElement | null;
	context: Context;
	baseUrl?: string;
}

interface EnvironmentInitializerProps {
	env: PluginEnv;
	variant: PluginVariant;
}

interface OverridesProps {
	overrides?: {
		moduleUri: string;
	};
}

type Props = PropsWithChildren<BaseProps> & EnvironmentInitializerProps & OverridesProps;
const Placement: FunctionComponent<Props> = ({
	env,
	variant,
	placementId,
	placementProps,
	children,
	ErrorFallback,
	Fallback,
	context,
	baseUrl,
	overrides,
}) => {
	const pluginRegistry = PluginRegistry.getRegistry();
	const plugin = pluginRegistry.getPluginForPlacement(placementId, env, variant, overrides);

	const stablePlacementProps = useStableObject(placementProps ?? {});
	const stableContext = useStableObject(context);

	const initialData = useMemo(
		() =>
			requestInitialData(placementId, stableContext, global.fetch, {
				baseUrl,
				path: baseUrl ? PLACEMENT_PATH : undefined,
			}),
		[baseUrl, placementId, stableContext],
	);

	const placementAnalyticsContext = useMemo(
		() => ({
			source: ANALYTICS_SOURCE,
			componentName: `Placement`,
			attributes: {
				placementId: placementId,
				channel: 'in-app',
			},
		}),
		[placementId],
	);

	return (
		<PostOfficeAnalyticsContext data={placementAnalyticsContext}>
			<RemoteComponent
				analyticsChannel={ANALYTICS_CHANNEL}
				remotePlugin={plugin}
				ErrorFallback={ErrorFallback}
				Fallback={Fallback}
				initialData={initialData}
				context={stableContext}
				baseUrl={baseUrl}
				{...stablePlacementProps}
			>
				{children}
			</RemoteComponent>
		</PostOfficeAnalyticsContext>
	);
};

export default Placement;
